import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useProAccess } from './useProAccess';

export type TypeDocument = 'carte_navigation' | 'attestation_assurance' | 'permis_navigation';

export interface DocumentBateau {
  id: string;
  user_id: string;
  bateau_id: string;
  type_document: TypeDocument;
  nom_fichier: string;
  chemin_stockage: string;
  taille_fichier: number;
  type_mime: string;
  created_at: string;
  updated_at: string;
}

export const useDocumentsBateaux = (bateauId?: string) => {
  const { user } = useAuth();
  const { isProAccount } = useProAccess();
  const [documents, setDocuments] = useState<DocumentBateau[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDocuments = async () => {
    if (!user || !bateauId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('documents_bateaux')
        .select('*')
        .eq('bateau_id', bateauId)
        .eq('user_id', user.id);

      if (error) throw error;
      setDocuments((data || []) as DocumentBateau[]);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les documents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadDocument = async (
    file: File,
    typeDocument: TypeDocument
  ): Promise<boolean> => {
    if (!user || !bateauId) return false;

    // Vérifier la limite de documents selon l'abonnement
    const maxDocs = isProAccount ? 5 : 1;
    const existingDocOfType = documents.find(d => d.type_document === typeDocument);
    // Si ce n'est pas un remplacement, vérifier la limite
    if (!existingDocOfType && documents.length >= maxDocs) {
      toast({
        title: "Limite atteinte",
        description: isProAccount
          ? "Vous avez atteint la limite de 5 documents par bateau."
          : "Les comptes gratuits sont limités à 1 document par bateau. Passez à l'abonnement Premium pour 5 documents.",
        variant: "destructive",
      });
      return false;
    }

    // Vérifier la taille du fichier (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille du fichier ne doit pas dépasser 5MB",
        variant: "destructive",
      });
      return false;
    }

    // Vérifier le type de fichier
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Format non supporté",
        description: "Seuls les fichiers PDF, JPEG et PNG sont acceptés",
        variant: "destructive",
      });
      return false;
    }

    setLoading(true);
    try {
      // Supprimer l'ancien document du même type s'il existe
      const existingDoc = documents.find(d => d.type_document === typeDocument);
      if (existingDoc) {
        await supprimerDocument(existingDoc.id);
      }

      // Générer un chemin unique: user_id/bateau_id/type_document/filename
      const fileExt = file.name.split('.').pop();
      const uniqueFileName = `${Date.now()}.${fileExt}`;
      const storagePath = `${user.id}/${bateauId}/${typeDocument}/${uniqueFileName}`;

      // Upload vers Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('boat-documents')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Enregistrer les métadonnées dans la base de données
      const { error: dbError } = await supabase
        .from('documents_bateaux')
        .insert({
          user_id: user.id,
          bateau_id: bateauId,
          type_document: typeDocument,
          nom_fichier: file.name,
          chemin_stockage: storagePath,
          taille_fichier: file.size,
          type_mime: file.type,
        });

      if (dbError) throw dbError;

      toast({
        title: "Document ajouté",
        description: "Le document a été enregistré avec succès",
      });

      await fetchDocuments();
      return true;
    } catch (error: any) {
      console.error('Erreur lors de l\'upload:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'enregistrer le document",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const supprimerDocument = async (documentId: string): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    try {
      // Récupérer le document pour avoir le chemin de stockage
      const docToDelete = documents.find(d => d.id === documentId);
      
      if (docToDelete && docToDelete.chemin_stockage) {
        // Supprimer de Supabase Storage
        const { error: storageError } = await supabase.storage
          .from('boat-documents')
          .remove([docToDelete.chemin_stockage]);

        if (storageError) {
          console.error('Erreur suppression storage:', storageError);
        }
      }

      // Supprimer de la base de données
      const { error: dbError } = await supabase
        .from('documents_bateaux')
        .delete()
        .eq('id', documentId);

      if (dbError) throw dbError;

      toast({
        title: "Document supprimé",
        description: "Le document a été supprimé avec succès",
      });

      await fetchDocuments();
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le document",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getDocumentUrl = async (chemin_stockage: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('boat-documents')
        .createSignedUrl(chemin_stockage, 3600); // URL valide 1 heure

      if (error) throw error;
      
      // L'URL signée est déjà complète avec le SDK Supabase
      // Mais on s'assure qu'elle est bien absolue
      if (data?.signedUrl) {
        // Si l'URL est relative, on ajoute le domaine Supabase
        if (data.signedUrl.startsWith('/')) {
          return `https://ivjevyrhkbinqtpvwbze.supabase.co/storage/v1${data.signedUrl}`;
        }
        return data.signedUrl;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la génération de l\'URL:', error);
      return null;
    }
  };

  const downloadDocument = async (doc: DocumentBateau): Promise<Blob | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('boat-documents')
        .download(doc.chemin_stockage);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [user, bateauId]);

  return {
    documents,
    loading,
    uploadDocument,
    supprimerDocument,
    getDocumentUrl,
    downloadDocument,
    fetchDocuments,
  };
};
